import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useEffect } from 'react'

interface MobileMenuOverlayProps {
	isMenuOpen: boolean
	isMenuClosing: boolean
	handleMenuClose: () => void
	menuItems: Array<{
		name: string
		path: string
		subItems: Array<{
			name: string
			path: string
		}>
	}>
}

const MobileMenuOverlay = ({
	isMenuOpen,
	isMenuClosing,
	handleMenuClose,
	menuItems,
}: MobileMenuOverlayProps) => {
	const { t } = useTranslation()

	// 메뉴가 열릴 때 body 스크롤 막기
	useEffect(() => {
		if (isMenuOpen) {
			// 현재 스크롤 위치 저장
			const scrollY = window.scrollY
			document.body.style.position = 'fixed'
			document.body.style.top = `-${scrollY}px`
			document.body.style.width = '100%'
		} else {
			// 메뉴가 닫힐 때 원래 스크롤 위치로 복원
			const scrollY = document.body.style.top
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.width = ''
			if (scrollY) {
				window.scrollTo(0, parseInt(scrollY || '0') * -1)
			}
		}

		// 컴포넌트 언마운트 시 정리
		return () => {
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.width = ''
		}
	}, [isMenuOpen])

	if (!isMenuOpen && !isMenuClosing) return null

	return (
		<div
			className={`fixed inset-0 z-50 bg-black bg-opacity-95 backdrop-blur-sm overflow-hidden ${
				isMenuClosing ? 'animate-fadeOut' : 'animate-fadeIn'
			}`}
		>
			<div className="h-screen max-h-screen overflow-hidden grid grid-rows-4 md:grid-rows-1 md:grid-cols-4">
				{/* 프로젝트 */}
				<div
					className={`relative flex flex-col justify-center items-center text-white group cursor-pointer hover:scale-105 transition-all duration-500 overflow-hidden h-full max-h-[25vh] md:max-h-screen ${
						isMenuClosing
							? 'animate-slideOutUp md:animate-slideOutLeft'
							: 'animate-slideInDown md:animate-slideInLeft'
					}`}
					style={{
						animationDelay: isMenuClosing ? '0.3s' : '0.1s',
					}}
				>
					{/* 배경 이미지 레이어 */}
					<div
						className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
						style={{
							backgroundImage:
								'url("https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80")',
						}}
					></div>

					{/* 그라데이션 오버레이 */}
					<div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40"></div>

					{/* 컬러 오버레이 그라데이션 */}
					<div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 to-blue-900/50 group-hover:from-purple-800/40 group-hover:to-blue-800/60 transition-all duration-500"></div>

					{/* 컨텐츠 */}
					<div className="relative z-10 text-left px-4 md:px-8 py-2 md:py-0 h-full flex flex-col justify-center max-h-[33.333vh] md:max-h-screen">
						<h2 className="text-xl md:text-4xl font-bold mb-2 md:mb-8 group-hover:scale-110 transition-transform duration-300 text-center">
							{t('header.project')}
						</h2>
						<div className="space-y-2 md:space-y-4 overflow-hidden">
							{menuItems[0].subItems.map((subItem, index) => (
								<Link
									key={index}
									to={subItem.path}
									onClick={handleMenuClose}
									className="group/item relative block text-sm md:text-xl font-medium text-white/80 hover:text-purple-300 transition-all duration-500 transform hover:translate-x-2 md:hover:translate-x-4 hover:scale-105 md:hover:scale-110 px-2 md:px-4 py-0.5 md:py-2 rounded-lg hover:bg-white/10 backdrop-blur-sm"
									style={{
										animationDelay: `${index * 0.1}s`,
										transform: 'translateY(20px)',
										opacity: 0,
										animation: 'slideInFadeUp 0.6s ease-out forwards',
									}}
								>
									{/* 좌측 장식 바 */}
									<span className="absolute left-0 top-0 h-full w-1 bg-purple-400 scale-y-0 group-hover/item:scale-y-100 transition-transform duration-300 origin-top rounded-r"></span>

									{/* 아이콘 */}
									<span className="inline-block mr-3 text-purple-300 group-hover/item:text-purple-200 transition-colors duration-300">
										▶
									</span>

									{/* 텍스트 */}
									<span className="relative">
										{subItem.name}
										{/* 밑줄 효과 */}
										<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-purple-300 group-hover/item:w-full transition-all duration-300"></span>
									</span>
								</Link>
							))}
						</div>
					</div>
				</div>

				{/* ABOUT 컬럼 */}
				{/* ABOUT 컬럼 */}
				<div
					className={`relative flex flex-col justify-center items-center text-white group cursor-pointer hover:scale-105 transition-all duration-500 overflow-hidden h-full max-h-[33.333vh] md:max-h-screen ${
						isMenuClosing
							? 'animate-slideOutUp md:animate-slideOutLeft'
							: 'animate-slideInDown md:animate-slideInLeft'
					}`}
					style={{
						animationDelay: isMenuClosing ? '0.3s' : '0.1s',
					}}
				>
					{/* 배경 이미지 레이어 */}
					<div
						className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
						style={{
							backgroundImage:
								'url("https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80")',
						}}
					></div>
					{/* 그라데이션 오버레이 */}
					<div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40"></div>
					{/* 컬러 오버레이 그라데이션 */}
					<div className="absolute inset-0 bg-gradient-to-br from-orange-900/30 to-red-900/50 group-hover:from-orange-800/40 group-hover:to-red-800/60 transition-all duration-500"></div>{' '}
					{/* 컨텐츠 */}
					<div className="relative z-10 text-left px-4 md:px-8 py-2 md:py-0 h-full flex flex-col justify-center max-h-[33.333vh] md:max-h-screen">
						<h2 className="text-xl md:text-4xl font-bold mb-2 md:mb-8 group-hover:scale-110 transition-transform duration-300 text-center">
							{t('header.about')}
						</h2>
						<div className="space-y-2 md:space-y-4 overflow-hidden">
							{menuItems[1].subItems.map((subItem, index) => (
								<Link
									key={index}
									to={subItem.path}
									onClick={handleMenuClose}
									className="group/item relative block text-sm md:text-xl font-medium text-white/80 hover:text-orange-300 transition-all duration-500 transform hover:translate-x-2 md:hover:translate-x-4 hover:scale-105 md:hover:scale-110 px-2 md:px-4 py-0.5 md:py-2 rounded-lg hover:bg-white/10 backdrop-blur-sm"
									style={{
										animationDelay: `${index * 0.1}s`,
										transform: 'translateY(20px)',
										opacity: 0,
										animation: 'slideInFadeUp 0.6s ease-out forwards',
									}}
								>
									{/* 좌측 장식 바 */}
									<span className="absolute left-0 top-0 h-full w-1 bg-orange-400 scale-y-0 group-hover/item:scale-y-100 transition-transform duration-300 origin-top rounded-r"></span>

									{/* 아이콘 */}
									<span className="inline-block mr-3 text-orange-300 group-hover/item:text-orange-200 transition-colors duration-300">
										▶
									</span>

									{/* 텍스트 */}
									<span className="relative">
										{subItem.name}
										{/* 밑줄 효과 */}
										<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-300 group-hover/item:w-full transition-all duration-300"></span>
									</span>
								</Link>
							))}
						</div>
					</div>
				</div>

				{/* 회사개요 컬럼 */}
				<div
					className={`relative flex flex-col justify-center items-center text-white group cursor-pointer hover:scale-105 transition-all duration-500 overflow-hidden h-full max-h-[25vh] md:max-h-screen ${
						isMenuClosing
							? 'animate-slideOutUp md:animate-slideOutDown'
							: 'animate-slideInDown md:animate-slideInUp'
					}`}
					style={{
						animationDelay: isMenuClosing ? '0.2s' : '0.2s',
					}}
				>
					{/* 배경 이미지 레이어 */}
					<div
						className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
						style={{
							backgroundImage:
								'url("https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80")',
						}}
					></div>

					{/* 그라데이션 오버레이 */}
					<div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40"></div>

					{/* 컬러 오버레이 그라데이션 */}
					<div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 to-teal-900/50 group-hover:from-emerald-800/40 group-hover:to-teal-800/60 transition-all duration-500"></div>

					{/* 컨텐츠 */}
					<div className="relative z-10 text-left px-4 md:px-8 py-2 md:py-0 h-full flex flex-col justify-center max-h-[33.333vh] md:max-h-screen">
						<h2 className="text-xl md:text-4xl font-bold mb-2 md:mb-8 group-hover:scale-110 transition-transform duration-300 text-center">
							{t('header.company')}
						</h2>
						<div className="space-y-2 md:space-y-4 overflow-hidden">
							{menuItems[2].subItems.map((subItem, index) => (
								<Link
									key={index}
									to={subItem.path}
									onClick={handleMenuClose}
									className="group/item relative block text-sm md:text-xl font-medium text-white/80 hover:text-emerald-300 transition-all duration-500 transform hover:translate-x-2 md:hover:translate-x-4 hover:scale-105 md:hover:scale-110 px-2 md:px-4 py-0.5 md:py-2 rounded-lg hover:bg-white/10 backdrop-blur-sm"
									style={{
										animationDelay: `${index * 0.1 + 0.2}s`,
										transform: 'translateY(20px)',
										opacity: 0,
										animation: 'slideInFadeUp 0.6s ease-out forwards',
									}}
								>
									{/* 좌측 장식 바 */}
									<span className="absolute left-0 top-0 h-full w-1 bg-emerald-400 scale-y-0 group-hover/item:scale-y-100 transition-transform duration-300 origin-top rounded-r"></span>

									{/* 아이콘 */}
									<span className="inline-block mr-3 text-emerald-300 group-hover/item:text-emerald-200 transition-colors duration-300">
										●
									</span>

									{/* 텍스트 */}
									<span className="relative">
										{subItem.name}
										{/* 밑줄 효과 */}
										<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-300 group-hover/item:w-full transition-all duration-300"></span>
									</span>
								</Link>
							))}
						</div>
					</div>
				</div>

				{/* CONTACT 컬럼 */}
				<div
					className={`relative flex flex-col justify-center items-center text-white group cursor-pointer hover:scale-105 transition-all duration-500 overflow-hidden h-full max-h-[25vh] md:max-h-screen ${
						isMenuClosing
							? 'animate-slideOutUp md:animate-slideOutRight'
							: 'animate-slideInDown md:animate-slideInRight'
					}`}
					style={{
						animationDelay: isMenuClosing ? '0.1s' : '0.3s',
					}}
				>
					{/* 배경 이미지 레이어 */}
					<div
						className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
						style={{
							backgroundImage:
								'url("https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80")',
						}}
					></div>

					{/* 그라데이션 오버레이 */}
					<div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/40"></div>

					{/* 컬러 오버레이 그라데이션 */}
					<div className="absolute inset-0 bg-gradient-to-br from-yellow-900/30 to-amber-900/50 group-hover:from-yellow-800/40 group-hover:to-amber-800/60 transition-all duration-500"></div>

					{/* 컨텐츠 */}
					<div className="relative z-10 text-left px-4 md:px-8 py-2 md:py-0 h-full flex flex-col justify-center max-h-[33.333vh] md:max-h-screen">
						<h2 className="text-xl md:text-4xl font-bold mb-2 md:mb-8 group-hover:scale-110 transition-transform duration-300 text-center">
							{t('header.contact')}
						</h2>
						<div className="space-y-2 md:space-y-4 overflow-hidden">
							{menuItems[3].subItems.map((subItem, index) => (
								<Link
									key={index}
									to={subItem.path}
									onClick={handleMenuClose}
									className="group/item relative block text-sm md:text-xl font-medium text-white/80 hover:text-yellow-300 transition-all duration-500 transform hover:translate-x-2 md:hover:translate-x-4 hover:scale-105 md:hover:scale-110 px-2 md:px-4 py-0.5 md:py-2 rounded-lg hover:bg-white/10 backdrop-blur-sm"
									style={{
										animationDelay: `${index * 0.1 + 0.4}s`,
										transform: 'translateY(20px)',
										opacity: 0,
										animation: 'slideInFadeUp 0.6s ease-out forwards',
									}}
								>
									{/* 좌측 장식 바 */}
									<span className="absolute left-0 top-0 h-full w-1 bg-yellow-400 scale-y-0 group-hover/item:scale-y-100 transition-transform duration-300 origin-top rounded-r"></span>

									{/* 아이콘 */}
									<span className="inline-block mr-3 text-yellow-300 group-hover/item:text-yellow-200 transition-colors duration-300">
										◆
									</span>

									{/* 텍스트 */}
									<span className="relative">
										{subItem.name}
										{/* 밑줄 효과 */}
										<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-300 group-hover/item:w-full transition-all duration-300"></span>
									</span>
								</Link>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* 닫기 버튼 */}
			<button
				onClick={handleMenuClose}
				className={`absolute top-3 right-5 w-12 h-12 flex items-center justify-center text-white hover:text-red-400 transition-colors duration-300 z-20 ${
					isMenuClosing ? 'animate-fadeOutScale' : 'animate-fadeInScale'
				}`}
				style={{ animationDelay: isMenuClosing ? '0s' : '0.4s' }}
				aria-label="메뉴 닫기"
			>
				<svg
					className="w-8 h-8"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</button>
		</div>
	)
}

export default MobileMenuOverlay
